import React, { useEffect, useMemo, useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { emergencyAlarm, speak } from "@/lib/audio";
import { Siren, ShieldCheck, X, Navigation } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Global officer-safety panic banner. Shown to ALL users; anyone can clear an
// active alert but MUST provide an explanation of why it was activated.
export default function PanicBanner() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [clearing, setClearing] = useState(null);
  const [reason, setReason] = useState("");
  const seen = useRef(new Set());

  const load = async () => {
    try {
      const { data } = await client.get("/panic/active");
      // Fire the alarm the first time we see any new active alert.
      const fresh = data.some((a) => !seen.current.has(a.id));
      if (fresh && data.length) {
        try { emergencyAlarm(); speak("Officer needs assistance. Panic alert activated.", { rate: 1.1 }); } catch { /* noop */ }
      }
      data.forEach((a) => seen.current.add(a.id));
      setAlerts(data);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, []);

  // Live responder ETAs — refresh every 15s (OSRM-backed) keyed on the roll-call shape
  // so the interval isn't reset by the 4s banner poll.
  const [etas, setEtas] = useState({});
  const rollKey = useMemo(
    () => alerts.map((a) => `${a.id}:${(a.responders || []).length}`).join(","),
    [alerts]);
  const alertsRef = useRef(alerts);
  alertsRef.current = alerts;
  useEffect(() => {
    if (!rollKey || !rollKey.split(",").some((k) => Number(k.split(":")[1]) > 0)) return;
    let stop = false;
    const poll = async () => {
      for (const a of alertsRef.current) {
        if (!(a.responders || []).length) continue;
        try {
          const { data } = await client.get(`/panic/${a.id}/responder-etas`);
          if (stop) return;
          setEtas((prev) => {
            const n = { ...prev };
            data.etas.forEach((e) => { n[`${a.id}:${e.unit}`] = e; });
            return n;
          });
        } catch { /* alert cleared or transient */ }
      }
    };
    poll();
    const iv = setInterval(poll, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, [rollKey]);
  const etaLabel = (a, unit) => {
    const e = etas[`${a.id}:${unit}`];
    if (!e || e.duration_s == null) return null;
    const mins = Math.round(e.duration_s / 60);
    return mins < 1 ? "<1 min" : `${mins} min`;
  };

  const respond = async (a) => {
    try {
      await client.post(`/panic/${a.id}/respond`);
      toast.success(`${user?.badge} marked RESPONDING — dispatch notified`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark responding"); }
  };

  const doClear = async (a) => {
    if (!reason.trim()) { toast.error("Explanation is required to clear the panic alert"); return; }
    try {
      await client.post(`/panic/${a.id}/clear`, { reason: reason.trim() });
      toast.success("Panic alert cleared");
      setClearing(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear"); }
  };

  if (alerts.length === 0) return null;

  return (
    <div data-testid="panic-banner" className="shrink-0">
      {alerts.map((a) => (
        <div key={a.id} className="relative bg-[#DC2626] text-white border-b-2 border-[#7f1d1d] anim-pulse-alert">
          <span className="absolute inset-y-0 left-0 w-10 tac-stripes opacity-20 pointer-events-none" />
          <div className="flex items-center gap-3 px-4 py-2 relative">
            <Siren size={18} className="animate-pulse shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-display font-black text-sm tracking-wide leading-none uppercase">
                Emergency Panic — {a.unit ? `${a.unit} · ` : ""}{a.by} Needs Assistance
              </div>
              <div className="font-mono text-[10px] opacity-90 mt-0.5">
                Activated {new Date(a.ts).toLocaleTimeString()} · open-mic per policy
                {a.lat ? ` · GPS ${a.lat.toFixed(4)}, ${a.lng.toFixed(4)}` : ""}
              </div>
              {(a.responders || []).length > 0 && (
                <div data-testid={`panic-responders-${a.id}`} className="font-mono text-[10px] mt-1 bg-black/25 inline-flex items-center gap-1.5 px-2 py-0.5 rounded flex-wrap">
                  <Navigation size={11} className="shrink-0" />
                  <span className="font-bold">RESPONDING ({a.responders.length}):</span>
                  {a.responders.map((r, i) => {
                    const eta = etaLabel(a, r.unit);
                    return (
                      <span key={r.unit} data-testid={`panic-responder-eta-${a.id}-${r.unit}`}>
                        {i > 0 && <span className="opacity-60 mr-1.5">•</span>}
                        {r.unit}{eta && <b className="ml-1 bg-white/90 text-[#DC2626] px-1 rounded-sm">{eta}</b>}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            {user?.badge && user.badge !== a.unit && !(a.responders || []).some((r) => r.unit === user.badge) && (
              <button data-testid={`panic-respond-${a.id}`} onClick={() => respond(a)}
                className="px-3 py-1.5 text-xs font-head font-bold bg-white text-[#DC2626] hover:bg-white/90 transition-colors flex items-center gap-1.5">
                <Navigation size={13} /> RESPONDING
              </button>
            )}
            {clearing === a.id ? (
              <div className="flex items-center gap-1.5">
                <input data-testid={`panic-clear-reason-${a.id}`} autoFocus value={reason}
                  onChange={(e) => setReason(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doClear(a)}
                  placeholder="Reason for activation…"
                  className="w-56 bg-white/95 text-black px-2 py-1 text-xs font-mono outline-none" />
                <button data-testid={`panic-clear-confirm-${a.id}`} onClick={() => doClear(a)}
                  className="px-2.5 py-1 text-xs font-head bg-black/30 hover:bg-black/50 flex items-center gap-1"><ShieldCheck size={12} /> Clear</button>
                <button data-testid={`panic-clear-cancel-${a.id}`} onClick={() => { setClearing(null); setReason(""); }}
                  className="p-1 hover:bg-black/30"><X size={14} /></button>
              </div>
            ) : (
              <button data-testid={`panic-clear-${a.id}`} onClick={() => { setClearing(a.id); setReason(""); }}
                className="px-3 py-1.5 text-xs font-head bg-black/25 hover:bg-black/40 border border-white/30 transition-colors">
                Clear Panic (explanation required)
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
